/**
 * The composer.
 *
 * A native <textarea>, not a rich-text editor. That is a correctness call, not
 * a shortcut: contenteditable editors routinely break IME composition, and
 * German input relies on dead keys and (on many layouts) composition for
 * umlauts. `isComposing` is checked before treating Enter as submit — without
 * it, typing "ü" can send the message mid-word.
 */
import { ArrowUp, Square } from 'lucide-react'
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Tooltip } from '@/components/ui/overlays'
import { cn } from '@/lib/utils'

const MAX_HEIGHT = 320

export function Composer({
  onSubmit,
  onStop,
  busy,
  placeholder,
  autoFocus,
  hint,
  leading,
}: {
  onSubmit: (text: string) => void
  onStop?: () => void
  busy: boolean
  placeholder?: string
  autoFocus?: boolean
  hint?: React.ReactNode
  /** Sits on the bottom row of the box, opposite the send button. */
  leading?: React.ReactNode
}) {
  const [value, setValue] = useState('')
  const ref = useRef<HTMLTextAreaElement>(null)

  /**
   * Grow to fit, then scroll.
   *
   * Measured against scrollHeight rather than a line count, so it stays correct
   * with wrapped German compounds.
   *
   * THE PLACEHOLDER IS REMOVED BEFORE MEASURING. In Chromium a textarea's
   * `scrollHeight` includes the placeholder, and the placeholder wraps: on the
   * first layout pass — before the flex row has distributed its width — the box
   * is 8px wide, "Ask about your meetings…" wraps to twenty-one lines, and the
   * empty composer measures 452px. That clamps to MAX_HEIGHT and sticks, so
   * every load opened with a 320px-tall empty input. Measured, not guessed: the
   * same element with the placeholder removed reads 29.
   *
   * The height is also CLEARED rather than set to zero, so `scrollHeight`
   * cannot come back as the box's previous scroll size. `height: auto` equals
   * the content height only because the row is `items-end` and the textarea
   * therefore does not stretch; if that becomes `items-stretch`, this measures
   * the space available instead.
   */
  const fit = useCallback(() => {
    const element = ref.current
    if (!element) return
    const placeholder = element.placeholder
    element.placeholder = ''
    element.style.overflowY = 'hidden'
    element.style.height = ''
    void element.offsetHeight
    const content = element.scrollHeight
    element.placeholder = placeholder
    element.style.height = `${Math.min(content, MAX_HEIGHT)}px`
    element.style.overflowY = content > MAX_HEIGHT ? 'auto' : 'hidden'
  }, [])

  useLayoutEffect(fit, [value, fit])

  /**
   * Re-fit whenever the box changes WIDTH.
   *
   * Three things need this and none of them is a keystroke: the first real
   * layout after mount, the panel opening beside the chat, and a window
   * resize. All three change how the same text wraps, so a height measured
   * before them is wrong until the next character is typed.
   *
   * Guarded on width because the observer also fires for the height changes
   * `fit` itself makes, which would otherwise be a loop.
   */
  useEffect(() => {
    const element = ref.current
    if (!element) return
    let width = -1
    const observer = new ResizeObserver(() => {
      if (element.clientWidth === width) return
      width = element.clientWidth
      fit()
    })
    observer.observe(element)
    return () => observer.disconnect()
  }, [fit])

  useEffect(() => {
    if (autoFocus) ref.current?.focus()
  }, [autoFocus])

  function submit() {
    const text = value.trim()
    if (!text || busy) return
    onSubmit(text)
    setValue('')
    requestAnimationFrame(() => ref.current?.focus())
  }

  return (
    <div className="space-y-1.5">
      {/* The scope control and the send button share a row UNDER the text, not
          beside it. Beside it they drift down as the box grows, and the scope
          would end up level with the middle of a long question rather than with
          the action it applies to. */}
      <div
        className={cn(
          'rounded-[var(--radius-xl)] border border-[var(--color-border-strong)]',
          'bg-[var(--color-surface)] px-2 pt-2 pb-1.5 transition-[border-color,box-shadow]',
          'focus-within:border-[var(--color-primary)] focus-within:ring-2 focus-within:ring-[var(--color-primary)]/20'
        )}
      >
        <textarea
          ref={ref}
          value={value}
          onChange={(event) => setValue(event.target.value)}
          onKeyDown={(event) => {
            // `nativeEvent.isComposing` is the IME guard — see the header.
            if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
              event.preventDefault()
              submit()
            }
          }}
          rows={1}
          placeholder={placeholder ?? 'Ask about your meetings…'}
          className={
            'scrollbar-none block min-h-[1.5rem] w-full resize-none border-0 bg-transparent px-1 py-1 ' +
            'text-sm leading-relaxed text-[var(--color-fg)] outline-none ' +
            'placeholder:text-[var(--color-fg-subtle)]'
          }
        />

        <div className="mt-1 flex items-center gap-2">
          {leading}
          <div className="flex-1" />
          {busy && onStop ? (
            <Tooltip content="Stop">
              <Button size="icon" variant="secondary" onClick={onStop} aria-label="Stop">
                <Square className="size-3 fill-current" />
              </Button>
            </Tooltip>
          ) : (
            <Button
              size="icon"
              variant="primary"
              onClick={submit}
              disabled={!value.trim()}
              aria-label="Send"
            >
              <ArrowUp className="size-4" />
            </Button>
          )}
        </div>
      </div>
      {hint ? <div className="px-2 text-2xs text-[var(--color-fg-subtle)]">{hint}</div> : null}
    </div>
  )
}
